import React from 'react';
import { Download, FileText, Image as ImageIcon, Video, Music, File } from 'lucide-react';
// Escapes HTML before the markdown pass and only links http(s)/mailto (review 3.3).
import { renderMessageMarkdown, safeHref } from '@/lib/messageMarkdown';

interface Attachment {
  originalName: string;
  url: string;
  key: string;
  size: number;
  contentType: string;
}

interface MessageRendererProps {
  content: string;
  attachments?: Attachment[] | null;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (contentType: string) => {
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType.startsWith('video/')) return Video;
  if (contentType.startsWith('audio/')) return Music;
  if (contentType.includes('pdf') || contentType.includes('document')) return FileText;
  return File;
};

export default function MessageRenderer({ content, attachments }: MessageRendererProps) {
  return (
    <div>
      {/* Message Content with Markdown Support */}
      <div 
        className="text-slate-300 text-sm leading-relaxed mb-3"
        dangerouslySetInnerHTML={{ __html: renderMessageMarkdown(content) }}
      />

      {/* Attachments Display */}
      {attachments && attachments.length > 0 && (
        <div className="mt-3 space-y-2">
          {attachments.map((attachment, index) => {
            const IconComponent = getFileIcon(attachment.contentType);
            const isImage = attachment.contentType.startsWith('image/');
            
            return (
              <div key={index} className="border border-slate-600 rounded-lg overflow-hidden bg-slate-800/50">
                {isImage ? (
                  // Image Preview
                  <div className="relative">
                    <img 
                      src={attachment.url} 
                      alt={attachment.originalName}
                      className="max-w-full max-h-64 object-cover rounded-t-lg"
                      loading="lazy"
                    />
                    <div className="p-3 bg-slate-800/90">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <ImageIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-300">{attachment.originalName}</span>
                          <span className="text-xs text-slate-500">({formatFileSize(attachment.size)})</span>
                        </div>
                        <a 
                          href={safeHref(attachment.url)} 
                          download={attachment.originalName}
                          className="text-amber-400 hover:text-amber-300 p-1 rounded hover:bg-slate-700"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  // File Preview
                  <div className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-slate-700 rounded">
                          <IconComponent className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-slate-200">{attachment.originalName}</div>
                          <div className="text-xs text-slate-500">{formatFileSize(attachment.size)} • {attachment.contentType}</div>
                        </div>
                      </div>
                      <a 
                        href={safeHref(attachment.url)} 
                        download={attachment.originalName}
                        className="text-amber-400 hover:text-amber-300 p-2 rounded hover:bg-slate-700 transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}